/**
 * Voice Capture Page
 * 
 * Allows users to record audio directly in the web app using the Native Voice Capture feature.
 * Recordings are uploaded to GCS and automatically transcribed using Gemini 2.0 Flash.
 * 
 * Part of Epic 13: Native Voice Capture
 */

import { useState } from 'react';
import { AudioRecorder } from '../components/audio-recorder';
import { RecordingList } from '../components/recording-management';
import type { Recording } from '../components/recording-management/types';

export function VoiceCapturePage() {
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [activeTab, setActiveTab] = useState<'record' | 'history'>('record');

  const handleRecordingComplete = (audioId: string) => {
    // Add new recording to the list
    const newRecording: Recording = {
      id: audioId,
      title: `Recording ${new Date().toLocaleString()}`,
      duration: 0, // Will be updated after processing
      size: 0, // Will be updated after processing
      format: 'webm', // Default web format
      sampleRate: 48000, // Default sample rate
      recordedAt: new Date().toISOString(),
      status: 'processing',
      sourceType: 'web-recording',
    };

    setRecordings(prev => [newRecording, ...prev]);
    
    // Switch to history tab to show the new recording
    setActiveTab('history');
  };

  const handleDelete = async (id: string) => {
    // TODO: Call API to delete recording
    setRecordings(prev => prev.filter(r => r.id !== id));
  };

  const handleDownload = (id: string, type: 'audio' | 'transcript') => {
    const recording = recordings.find(r => r.id === id);
    if (!recording) return;

    if (type === 'audio' && recording.audioUrl) {
      window.open(recording.audioUrl, '_blank');
    } else if (type === 'transcript' && recording.transcriptUrl) {
      window.open(recording.transcriptUrl, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Voice Capture
          </h1>
          <p className="text-gray-600">
            Record your thoughts, ideas, and reflections. Your audio will be automatically transcribed and available in your journal.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('record')}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm
                  ${activeTab === 'record'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                Record
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm
                  ${activeTab === 'history'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                History
                {recordings.length > 0 && (
                  <span className="ml-2 bg-gray-200 text-gray-700 py-0.5 px-2 rounded-full text-xs">
                    {recordings.length}
                  </span>
                )}
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'record' && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Record Audio
                </h2>
                <AudioRecorder 
                  config={{
                    autoUpload: true,
                    onUploadComplete: handleRecordingComplete
                  }}
                />
                
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="text-sm font-medium text-blue-900 mb-2">
                    Tips for Best Results
                  </h3>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Speak clearly and at a moderate pace</li>
                    <li>• Find a quiet environment to minimize background noise</li>
                    <li>• Keep your device's microphone unobstructed</li>
                    <li>• Recordings are automatically saved and transcribed</li>
                  </ul>
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Recording History
                  </h2>
                  {recordings.length > 0 && (
                    <span className="text-sm text-gray-500">
                      {recordings.length} recording{recordings.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {recordings.length === 0 ? (
                  <div className="text-center py-12">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                      />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No recordings yet</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Get started by recording your first audio message.
                    </p>
                    <div className="mt-6">
                      <button
                        onClick={() => setActiveTab('record')}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <svg
                          className="-ml-1 mr-2 h-5 w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Record Now
                      </button>
                    </div>
                  </div>
                ) : (
                  <RecordingList
                    recordings={recordings}
                    onDelete={handleDelete}
                    onDownload={handleDownload}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

